import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '../types/user.model';

export interface ActingAs {
  pk: string;
  name: string;
  email: string;
}

interface ImpersonationState {
  /** The admin's own session, parked so it can be restored on exit. */
  adminAccessToken: string;
  // The store's own refreshToken is `string | null` (see AuthState), so the
  // parked value carries the same nullability rather than a coalesced lie.
  adminRefreshToken: string | null;
  actingAs: ActingAs;
  takeover: boolean;
  /**
   * False when the backend refuses a takeover for this account (banned or
   * deleted). Optional because a session persisted before the field existed
   * rehydrates without it: read it as `canTakeOver !== false`, which offers the
   * button and lets the backend refuse.
   */
  canTakeOver?: boolean;
}

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: User | null;
  isLoadingUser: boolean;
  impersonation: ImpersonationState | null;
  setToken: (accessToken: string, refreshToken: string) => void;
  clearTokens: () => void;
  isAuthenticated: () => boolean;
  setUser: (user: User | null) => void;
  setIsLoadingUser: (isLoading: boolean) => void;
  startImpersonation: (payload: {
    accessToken: string;
    actingAs: ActingAs;
    takeover: boolean;
    canTakeOver: boolean;
  }) => boolean;
  upgradeImpersonation: (payload: { accessToken: string; takeover: boolean }) => void;
  stopImpersonation: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      accessToken: null,
      refreshToken: null,
      user: null,
      isLoadingUser: false,
      impersonation: null,
      setToken: (accessToken: string, refreshToken: string) =>
        set({ accessToken, refreshToken }),
      clearTokens: () =>
        set({
          accessToken: null,
          refreshToken: null,
          user: null,
          isLoadingUser: false,
          // Logging out is the one moment where "forget everything" is
          // unambiguously right — unlike stopImpersonation, whose job is to
          // restore rather than discard. Leaving this set would persist a
          // stale impersonation block: the next login would render the
          // support banner over a perfectly ordinary session.
          impersonation: null,
        }),
      isAuthenticated: () => {
        const { accessToken, refreshToken, impersonation } = get();
        // A support session deliberately carries no refresh token: that is what
        // makes it expire on its own. Without this branch ProtectedRoute would
        // send it to /login on the first navigation and impersonation would not
        // work at all.
        return !!accessToken && (!!refreshToken || impersonation !== null);
      },
      setUser: (user: User | null) => set({ user }),
      setIsLoadingUser: (isLoading: boolean) =>
        set({ isLoadingUser: isLoading }),

      // The borrowed token takes the place of the active session, which is why no
      // other screen needs to know impersonation exists. The admin's own tokens are
      // parked alongside it — losing them would strand the admin in the borrowed
      // session with no way back.
      //
      // Returns true only when the session was actually installed, and false on
      // either refusal below. Callers MUST branch on it before navigating into
      // the target's account: a refusal leaves the previous session in place, so
      // navigating anyway lands on one owner's tournament holding another
      // owner's token, which the backend then rejects. Mirrors
      // stopImpersonation, which returns a boolean for the same reason.
      startImpersonation: ({ accessToken, actingAs, takeover, canTakeOver }) => {
        // Already impersonating: refuse, don't overwrite. The parked tokens
        // belong to the admin's own session — replacing them with the current
        // target's tokens would strand the admin, unable to ever get back to
        // their own account.
        if (get().impersonation) return false;
        const { accessToken: adminAccessToken, refreshToken: adminRefreshToken } = get();
        if (!adminAccessToken) return false;
        set({
          impersonation: {
            adminAccessToken,
            adminRefreshToken,
            actingAs,
            takeover,
            canTakeOver,
          },
          accessToken,
          // The support session has no refresh token on purpose: that is what makes
          // it expire on its own instead of renewing indefinitely.
          refreshToken: null,
          user: null,
        });
        return true;
      },

      upgradeImpersonation: ({ accessToken, takeover }) => {
        const current = get().impersonation;
        if (!current) return;
        set({
          impersonation: { ...current, takeover },
          accessToken,
          refreshToken: null,
        });
      },

      // Returns false when there was nothing to restore, so callers can tell
      // "left the support session" from "was never in one".
      stopImpersonation: () => {
        const current = get().impersonation;
        if (!current) return false;
        set({
          impersonation: null,
          accessToken: current.adminAccessToken,
          refreshToken: current.adminRefreshToken,
          user: null,
        });
        return true;
      },
    }),
    {
      name: 'auth-storage',
      // KNOWN GAP, documented as `.claude/rules/security.md` (Token storage)
      // requires: this writes auth tokens to localStorage, which that rule and
      // coding-style.md (State) both say not to do. Any XSS on the domain reads
      // them. The backend issues bearer tokens rather than setting HttpOnly
      // cookies, so removing them from here means logging in again on every
      // reload; closing it properly is a backend change, not a partialize one.
      //
      // The impersonation block below makes the gap worse in one specific way
      // and it is worth being explicit about it. A support session is designed
      // to be the cheap credential to lose: short-lived, no refresh token,
      // read-only until an audited takeover. But the admin's OWN refresh token
      // is parked inside `impersonation` for the duration — so during support
      // the same localStorage key holds the highest-privilege credential on the
      // platform, and an XSS then reads it instead of the borrowed one.
      //
      // Kept anyway because the alternative is worse in practice: without it an
      // F5 mid-support strands the admin in the borrowed session with no way
      // back to their own account. Note that the admin's refresh token sits in
      // this same key outside support sessions too (as `refreshToken`), so what
      // this actually costs is the window, not the exposure.
      //
      // user and isLoadingUser stay in memory (runtime only).
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        // Persisted so an F5 mid-support does not drop the session — and, more
        // importantly, does not lose the admin's own parked tokens. Adding keys
        // is backward compatible: an older stored state rehydrates without it,
        // and its absence reads as "not impersonating".
        impersonation: state.impersonation,
      }),
    },
  ),
);

/**
 * Whether a support session is active. Lives next to the state it derives from
 * so the consumers of `impersonation` do not each rewrite the predicate.
 */
export const useIsImpersonating = () =>
  useAuthStore((state) => state.impersonation !== null);
